<template>
	<div v-if="ad && renderAd">
		<div
			v-show="adVisible"
			class="image-renderer"
			:class="{
				'is-mobile': isMobileWebApp,
				'is-portrait': isMobileWebAppPortrait,
				'is-landscape': isMobileWebAppLandscape,
				'monetization-logo': type === AdType.PLAYER_LOGO,
				'monetization-banner': type === AdType.PLAYER_BANNER,
				'cursor-pointer': adLink,
				'has-loaded': hasLoaded,
			}"
			:data-text="$I18n.trans('monetization.advertisement')"
			:style="style"
			@click.stop="clickHandler"
		>
			<AdButlerAd
				v-if="ad.isAdbutlerAd"
				:ad="ad"
				:fixed-size="!dynamicResizing"
				@loaded="adbutlerLoaded"
			/>
			<img
				v-else-if="adVisible"
				ref="image"
				class="image-renderer__image"
				:src="image"
				data-testid="adbutler-image"
			>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { EVENT, bus } from '@videotools/js/events';
import storage from '@shared/js/storage';
import AdButlerAd from '@components/Ads/AdButlerAd.vue';
import { AdType } from '@shared/enums/AdType';
import { AdRotationMode } from '@shared/enums/AdRotationMode';
import {
	getIntervalDisplaySlotIndex,
	isIntervalDisplayMode,
	isIntervalDisplayVisible,
	isOnceDisplayMode,
} from '@components/player/monetization/displayTiming';
import { getEffectiveCreativeRotationMode } from '@components/player/monetization/creativeRotation';

const PLAYER_AD = 'player_ad';

export default {
	name:       'MonetizationImageRenderer',
	components: { AdButlerAd },
	props:      {
		playerIsAdMode: {
			type:    Boolean,
			default: false,
		},
		position: {
			type: Number,
		},
		type: {
			type:      String,
			required:  true,
			validator: value => [AdType.PLAYER_LOGO, AdType.PLAYER_BANNER].includes(value),
		},
		playerWidth: {
			type:    Number,
			default: null,
		},
		event: {
			type:    Object,
			default: null,
		},
		ads: {
			type:    Array,
			default: () => [],
		},
		isCouch: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			timeout:               null,
			originalHeight:        null,
			originalWidth:         null,
			renderAd:              false,
			lastImageLoadedTs:     null,
			positionUpdated:       false,
			hasLoaded:             false,
			randomizedCreativeIds: [],
			randomizedPass:        null,
			AdType,
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:          'app/isMobileWebApp',
			isMobileWebAppPortrait:  'app/isMobileWebAppPortrait',
			isMobileWebAppLandscape: 'app/isMobileWebAppLandscape',
			isTaggingToolMode:       'app/isTaggingToolMode',
		}),
		style() {
			if (!this.playerWidth ||
				this.type !== AdType.PLAYER_LOGO ||
				!this.originalHeight ||
				!this.originalWidth
			) return {};

			// Default container 240x240 for 1920px width
			// todo controls2: scale in PlayerOverlays.vue
			const containerLimit = Math.round(this.playerWidth * 140 / 1920);
			const ratio = Math.min(containerLimit / this.originalWidth, containerLimit / this.originalHeight);

			return {
				width:  `${this.originalWidth * ratio}px`,
				height: `${this.originalHeight * ratio}px`,
			};
		},
		// Render ad based on position time
		adVisible() {
			if (
				this.playerIsAdMode ||
				this.isCouch ||
				this.isTaggingToolMode ||
				!this.hasMonetizationAds ||
				!this.renderAd ||
				!this.positionUpdated
			) {
				return false;
			}

			if (isIntervalDisplayMode(this.displayMode)) {
				return isIntervalDisplayVisible(this.displayMode, this.position, this.displayIntervalSeconds, this.displayDurationSeconds);
			}
			if (isOnceDisplayMode(this.displayMode)) {
				return Boolean(this.timeout);
			}
			return true; // Permanent;
		},
		displayMode() {
			return this.ad?.displayMode;
		},
		displayIntervalSeconds() {
			return this.ad?.displayIntervalSeconds;
		},
		displayDurationSeconds() {
			return this.ad?.displayDurationSeconds;
		},
		displaySlotIndex() {
			return getIntervalDisplaySlotIndex(this.displayMode, this.position, this.displayIntervalSeconds, this.displayDurationSeconds);
		},
		hasMonetizationAds() {
			return this.ads && this.ads.length;
		},
		image() {
			if (this.type === AdType.PLAYER_BANNER) {
				return this.currentCreative?.imageUrl || this.ad?.imageUrl;
			}
			return this.ad?.imageUrl;
		},
		adLink() {
			if (this.type === AdType.PLAYER_BANNER) {
				return this.currentCreative?.link || this.ad?.link;
			}
			return this.ad?.link;
		},
		hasVast() {
			return this.ads.find(ad => ad.type === PLAYER_AD);
		},
		ad() {
			return this.ads?.find(ad => ad.type === this.type);
		},
		creatives() {
			if (this.type !== AdType.PLAYER_BANNER) {
				return [];
			}
			const creatives = this.ad?.creatives?.length
				? this.ad.creatives
				: this.ad?.imageUrl
					? [{
						id:       'legacy',
						imageUrl: this.ad.imageUrl,
						link:     this.ad.link,
					}]
					: [];

			return [...creatives].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
		},
		currentCreative() {
			if (!this.creatives.length) {
				return null;
			}
			if (this.effectiveRotationMode === AdRotationMode.RANDOMIZED && this.randomizedCreativeIds.length) {
				const creativeId = this.randomizedCreativeIds[this.displaySlotIndex % this.randomizedCreativeIds.length];
				return this.creatives.find(creative => creative.id === creativeId) || this.creatives[0];
			}
			return this.creatives[this.displaySlotIndex % this.creatives.length];
		},
		effectiveRotationMode() {
			return getEffectiveCreativeRotationMode(this.displayMode, this.ad?.rotationMode, this.creatives.length);
		},
		dynamicResizing() {
			return this.type === AdType.PLAYER_LOGO;
		},
	},
	watch: {
		adVisible: function(visible) {
			this.lastImageLoadedTs = Date.now();
			if (visible) {
				this.$nextTick(() => {
					if (this.$refs.image && !this.hasLoaded) {
						this.$refs.image.onload = () => {
							this.hasLoaded = true;
						};
					}
				});
			}
		},
		position() {
			this.positionUpdated = true;
		},
		ad() {
			this.resetCreativeRotation();
			if (this.ad?.isAdbutlerAd) {
				this.renderAd = true;
			}
		},
		displaySlotIndex() {
			this.ensureRandomizedOrder();
		},
		image() {
			this.hasLoaded = false;
		},
	},
	mounted() {
		this.resetCreativeRotation();
		if (this.image) {
			if (this.type === AdType.PLAYER_LOGO) {
				const img = new Image();
				const vm = this;
				img.onload = function() {
					vm.originalHeight = this.naturalHeight;
					vm.originalWidth = this.naturalWidth;
					vm.renderAd = true;
				};
				img.src = this.image;
			} else {
				this.renderAd = true;
			}

			this.$nextTick(() => {
				if (this.$refs.image && !this.hasLoaded) {
					this.$refs.image.onload = () => {
						this.hasLoaded = true;
					};
				}
			});
		}

		if (this.ad?.isAdbutlerAd) {
			this.renderAd = true;
		}

		if (isOnceDisplayMode(this.displayMode)) {
			if (!this.hasVast) {
				this.startTenSecondOnceDisplay();
			} else {
				bus.subscribe(EVENT.PLAYER.ON.MONETIZATION_AD_START, this.startTenSecondOnceDisplay);
			}
		}
	},
	destroyed() {
		clearTimeout(this.timeout);
		if (this.hasVast && isOnceDisplayMode(this.displayMode)) bus.unsubscribe(EVENT.PLAYER.ON.MONETIZATION_AD_START, this.startTenSecondOnceDisplay);
	},
	methods: {
		clickHandler() {
			if (this.adLink) window.open(this.appendAdAttributes(this.adLink), '_blank');
		},
		resetCreativeRotation() {
			this.randomizedCreativeIds = [];
			this.randomizedPass = null;
			this.ensureRandomizedOrder();
		},
		ensureRandomizedOrder() {
			if (this.type !== AdType.PLAYER_BANNER ||
					this.effectiveRotationMode !== AdRotationMode.RANDOMIZED ||
				!this.creatives.length
			) {
				return;
			}

			const pass = Math.floor(this.displaySlotIndex / this.creatives.length);
			if (this.randomizedPass === pass && this.randomizedCreativeIds.length === this.creatives.length) {
				return;
			}

			const previousLast = this.randomizedCreativeIds[this.randomizedCreativeIds.length - 1];
			const ids = this.creatives.map(creative => creative.id);
			for (let i = ids.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1));
				[ids[i], ids[j]] = [ids[j], ids[i]];
			}

			if (ids.length > 1 && ids[0] === previousLast) {
				[ids[0], ids[1]] = [ids[1], ids[0]];
			}

			this.randomizedCreativeIds = ids;
			this.randomizedPass = pass;
		},
		startTenSecondOnceDisplay() {
			const TEN_SECONDS_TIMEOUT = 10000;

			clearTimeout(this.timeout);
			this.timeout = setTimeout(() => {
				this.timeout = null;
			}, TEN_SECONDS_TIMEOUT);
		},
		appendAdAttributes(url, appendRandomHash = false) {
			if (!url) {
				return url;
			}

			let imageUrl = this.event ? `${url}/${this.event.id}` : url;

			if (this.event && storage.viewerId) {
				imageUrl = `${imageUrl}/${storage.viewerId}`;
			}

			if (appendRandomHash) {
				imageUrl = `${imageUrl}/${this.getCurrentTimeHash()}`;
			}

			return imageUrl;
		},
		getCurrentTimeHash() {
			return moment(this.lastImageLoadedTs || Date.now()).startOf('minute').unix();
		},
		adbutlerLoaded(adPlacement) {
			if (adPlacement) {
				this.hasLoaded = true;

				if (adPlacement.width && adPlacement.height) {
					this.originalWidth = adPlacement.width;
					this.originalHeight = adPlacement.height;
				}
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.cursor-pointer {
	cursor: pointer;
}

.monetization-banner {
	position: relative;
	color: var(--color-soft-white);
	margin: 0 auto;

	&:not(.is-mobile) {
		max-width: 728px;

		@media screen and (max-width: 1200px) {
			max-width: 80%;
		}
	}

	&.is-mobile {
		&.is-portrait {
			width: 100%;
			height: auto;
		}

		&.is-landscape {
			max-width: 80%;
			max-height: 60px;
		}
	}

	&.has-loaded::after {
		display: block;
		content: attr(data-text);
		position: absolute;
		right: 2px;
		bottom: calc(100% - -8px);
		font-size: 14px; // stylelint-disable-line sh-waqar/declaration-use-variable, property-disallowed-list
		color: var(--color-white);
	}

	&.has-loaded.is-mobile::after {
		position: relative;
		display: inline-block;
		float: right;
		right: 0;
		top: 0;
		padding: 8px 5px;
		background: none;
		color: var(--color-white);
		font-size: 12px; // stylelint-disable-line sh-waqar/declaration-use-variable, property-disallowed-list

		body.-t-light & { // stylelint-disable-line
			color: var(--color-black);
		}
	}
}

.image-renderer {
	&:empty {
		display: none;
	}
}

.image-renderer__image {
	.image-renderer.monetization-banner & {
		width: 100%;
	}
}
</style>
